import React from 'react';
import { Link } from 'react-router-dom';
import KeystoneLogo, { KeystoneMark } from '../components/KeystoneLogo';
import BrochureGate from '../components/BrochureGate';
import { downloadServerPDF } from '../lib/pdf-download';

/**
 * Abu Dhabi Entrepreneur Concept Deck — a short (10-page) nomination-focused
 * companion to the full Abu Dhabi Business Plan. Built for the ADRO/ADIO
 * approved pioneering-project concept route, which calls for a business
 * concept overview deck a reviewer can read in five minutes.
 *
 * Route: `/brochure/concept-deck/ad` — private, password-gated, download via
 * `downloadServerPDF()`. Shares the navy / magenta / gold Keystone identity.
 */

const TOTAL_PAGES = 10;

const C = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  magenta: '#E91E63',
  gold: '#C9A961',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  ink: '#0F1B2A',
  slate: '#3D4A5C',
  muted: '#6B7280',
  border: '#E2E8F0',
};

const Page = ({ children, bg = '#fff', last = false, pageNum, noPad = false }) => (
  <section
    className="brochure-page"
    data-page={pageNum}
    style={{
      width: '210mm',
      height: '297mm',
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page',
      boxShadow: '0 8px 32px rgba(10, 37, 64, 0.12)',
      margin: '0 auto 24px',
      padding: noPad ? 0 : '14mm 16mm',
      boxSizing: 'border-box',
      fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      color: C.ink,
    }}
  >
    {children}
  </section>
);

const PageHeader = ({ pageNum, dark = false }) => (
  <div
    style={{
      position: 'absolute',
      top: '8mm', left: '16mm', right: '16mm',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      fontSize: '8.5pt', color: dark ? 'rgba(255,255,255,0.65)' : C.muted,
      letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <KeystoneMark size={20} variant={dark ? 'dark' : 'light'} />
      <span style={{ color: dark ? '#fff' : C.navy, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', fontSize: '9.5pt' }}>Keystone</span>
      <span style={{ fontSize: '7.5pt' }}>·</span>
      <span style={{ color: dark ? C.gold : C.magenta, fontSize: '7.5pt', letterSpacing: '0.15em' }}>BY CLOUDVORO</span>
    </div>
    <span>Concept deck · {pageNum} / {TOTAL_PAGES}</span>
  </div>
);

const PageFooter = ({ dark = false, section }) => (
  <div
    style={{
      position: 'absolute',
      bottom: '8mm', left: '16mm', right: '16mm',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      fontSize: '7.5pt', color: dark ? 'rgba(255,255,255,0.55)' : C.muted, letterSpacing: '0.06em',
    }}
  >
    <span>Keystone by CloudVoro{section ? ` · ${section}` : ''}</span>
    <span>Confidential · Abu Dhabi Entrepreneur concept</span>
  </div>
);

const StepBadge = ({ n, label }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: '4mm' }}>
    <span style={{ fontSize: '9pt', letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta }}>
      {n}
    </span>
    <span style={{ fontSize: '8pt', letterSpacing: '0.2em', color: C.muted, fontWeight: 600 }}>·</span>
    <span style={{ fontSize: '8pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600, color: C.muted }}>
      {label}
    </span>
  </div>
);

const H2 = ({ children, style = {} }) => (
  <h2 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '28pt', fontWeight: 700, color: C.navy,
    lineHeight: 1.14, letterSpacing: '-0.018em', margin: '0 0 6mm',
    ...style,
  }}>
    {children}
  </h2>
);

const Body = ({ children, style = {} }) => (
  <p style={{ fontSize: '11pt', color: C.slate, lineHeight: 1.7, margin: '0 0 5mm', ...style }}>
    {children}
  </p>
);

// ==================== PAGE 1: Cover / one sentence ====================

const CoverPage = () => (
  <Page pageNum={1} bg={C.navy} noPad>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '42%',
        backgroundImage: "url('/press/cheese-maturation.jpg')",
        backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.2,
      }} aria-hidden="true" />
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '58%',
        background: `linear-gradient(90deg, ${C.navy} 30%, transparent 100%)`,
      }} aria-hidden="true" />

      <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeystoneMark size={22} variant="dark" />
          <span style={{ color: '#fff', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', fontSize: '10pt' }}>Keystone</span>
          <span style={{ fontSize: '8pt' }}>·</span>
          <span style={{ color: C.gold, fontSize: '8pt', letterSpacing: '0.18em' }}>BY CLOUDVORO</span>
        </div>
        <span>Abu Dhabi · 2026 &nbsp;|&nbsp; Concept deck · confidential</span>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '8mm', position: 'relative', zIndex: 2 }}>
        <div style={{ marginBottom: '9mm' }}>
          <KeystoneLogo variant="dark" size={44} />
        </div>
        <div style={{ fontSize: '10pt', letterSpacing: '0.3em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '7mm' }}>
          Abu Dhabi Entrepreneur &middot; Concept deck
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '40pt', fontWeight: 700, color: '#fff',
          lineHeight: 1.08, letterSpacing: '-0.022em', margin: 0, maxWidth: '168mm',
        }}>
          The operating system for<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>small food manufacturers.</em>
        </h1>

        <div style={{ marginTop: '9mm', padding: '6mm 7mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}`, maxWidth: '165mm' }}>
          <div style={{ fontSize: '8.5pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
            Keystone in one sentence
          </div>
          <div style={{ fontSize: '14pt', color: '#fff', lineHeight: 1.5, fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 500 }}>
            Keystone is a live, production-proven software platform that gives small and mid-sized food manufacturers enterprise-grade traceability, food-safety and retailer-dispatch capability, deployed in weeks and priced for operating budgets rather than enterprise transformation programmes.
          </div>
        </div>

        <div style={{ marginTop: '9mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm', maxWidth: '165mm' }}>
          {[
            { k: 'Live in production', v: 'Running a real, exporting food manufacturer every working day.' },
            { k: 'Built in Ireland', v: 'By a founder with a decade of GCC enterprise delivery.' },
            { k: 'GCC base: Abu Dhabi', v: 'Ready to establish its regional headquarters here.' },
          ].map((m, i) => (
            <div key={i}>
              <div style={{ fontSize: '9pt', letterSpacing: '0.14em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2mm' }}>{m.k}</div>
              <div style={{ fontSize: '9.5pt', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{m.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '8pt', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.06em', paddingTop: '6mm', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
        <span>Mohammed Asif Ali Khan · asif@cloudvoro.com · keystone.cloudvoro.com</span>
        <span>Cloudvoro Ltd · Ireland · CRO 795787</span>
      </div>
    </div>
  </Page>
);

// ==================== PAGE 2: The problem ====================

const ProblemPage = () => (
  <Page pageNum={2} bg={C.cream}>
    <PageHeader pageNum={2} />
    <div style={{ paddingTop: '18mm' }}>
      <StepBadge n="02" label="The food manufacturing problem" />
      <H2>
        Small producers carry a<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>multinational&rsquo;s obligations.</em>
      </H2>
      <Body>
        Every food manufacturer, regardless of size, must be able to trace a batch, prove food safety, satisfy an audit and produce retailer-ready dispatch paperwork. The software built to do that has only ever been priced and staffed for the largest producers. Everyone else faces two bad choices, an oversized ERP or a generic tool that has never seen a food factory, and most stay on paper.
      </Body>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { h: 'Paper and spreadsheets', b: 'Traceability exercises measured in hours. Lost batch records. Dispatch paperwork rebuilt by hand for every retailer.' },
          { h: 'Tier-one ERP', b: 'Months of implementation, consultants on retainer, capital budgets and an IT department the producer does not have.' },
          { h: 'Generic MRP', b: 'No GS1-128 or SSCC labelling, no certificate workflows, no food-grade lot depth. Not built for food.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '6mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.magenta}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', color: C.navy, fontWeight: 700, marginBottom: '3mm', letterSpacing: '-0.01em' }}>{c.h}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>{c.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '9mm', padding: '7mm 8mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.012em' }}>
          A recall does not ask how big you are.<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>It asks one question: can you prove it?</em>
        </div>
      </div>
    </div>
    <PageFooter section="Problem" />
  </Page>
);

// ==================== PAGE 3: What Keystone does ====================

const WhatPage = () => (
  <Page pageNum={3} bg={C.paper}>
    <PageHeader pageNum={3} />
    <div style={{ paddingTop: '18mm' }}>
      <StepBadge n="03" label="What Keystone does" />
      <H2>
        From intake to pallet,<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>on one unbroken record.</em>
      </H2>

      <div style={{ marginTop: '4mm', padding: '10mm 8mm', background: C.cream, border: `1px solid ${C.border}` }}>
        <svg viewBox="0 0 900 210" width="100%" height="150" preserveAspectRatio="xMidYMid meet">
          <line x1="80" y1="110" x2="820" y2="110" stroke={C.magenta} strokeWidth="2.5" strokeDasharray="8 5" opacity="0.6" />
          {[
            { x: 80, l: 'INTAKE', s: 'ingredient lots' },
            { x: 265, l: 'PRODUCTION', s: 'batches, yields' },
            { x: 450, l: 'QUALITY', s: 'CoC / CoA' },
            { x: 635, l: 'DISPATCH', s: 'docs, SSCC labels' },
            { x: 820, l: 'AUDIT', s: 'full history' },
          ].map((n, i) => (
            <g key={i}>
              <circle cx={n.x} cy="110" r="30" fill={C.navy} />
              <circle cx={n.x} cy="110" r="12" fill={C.gold} />
              <text x={n.x} y="52" fontSize="20" fill={C.navy} fontWeight="700" textAnchor="middle" letterSpacing="1.8">{n.l}</text>
              <text x={n.x} y="180" fontSize="14.5" fill={C.muted} textAnchor="middle">{n.s}</text>
            </g>
          ))}
        </svg>
        <div style={{ textAlign: 'center', marginTop: '6mm', fontSize: '9.5pt', letterSpacing: '0.24em', color: C.magenta, fontWeight: 700, textTransform: 'uppercase' }}>
          One unbroken traceability chain
        </div>
      </div>

      <div style={{ marginTop: '7mm', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '5mm' }}>
        {[
          { h: 'Operations', b: 'Production and batch management, ingredient and finished-product traceability, lot tracking, stock movement and returns, operational reporting. Self-learning calculations adapt to each customer, automatically applying case weights and units learned from real usage while allowing operators to override them whenever required.' },
          { h: 'Compliance', b: 'Quality and compliance records, certificate of conformance and analysis workflows, role-based permissions, complete audit history.' },
          { h: 'Dispatch, the wedge', b: 'Customer-specific requirements, retailer-template dispatch documentation, GS1-128 and SSCC pallet and product labels. The daily pain no generic tool touches.', accent: true },
          { h: 'Platform', b: 'React, FastAPI, MongoDB, AWS. REST integrations to accounting, weighing and labelling. Modular, so regional capability is added, never rebuilt.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderLeft: `4px solid ${c.accent ? C.magenta : C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', color: C.navy, fontWeight: 700, marginBottom: '3mm', letterSpacing: '-0.01em' }}>{c.h}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>{c.b}</div>
          </div>
        ))}
      </div>
    </div>
    <PageFooter section="Product" />
  </Page>
);

// ==================== PAGE 4: Live commercial proof ====================

const ProofPage = () => (
  <Page pageNum={4} bg={C.cream}>
    <PageHeader pageNum={4} />
    <div style={{ paddingTop: '18mm' }}>
      <StepBadge n="04" label="Live commercial proof" />
      <H2>
        One customer.<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>Every single working day.</em>
      </H2>
      <Body>
        Keystone runs the daily operations of an established, internationally exporting food manufacturer, real batches, real audits, real retailer dispatch paperwork, since early 2026. We report that as one customer, deliberately. A single manufacturer trusting a system with its production and its retailer relationships demonstrates an operating venture, not a concept.
      </Body>

      <div style={{ marginTop: '4mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { v: 'Live', k: 'In production', s: 'Running through every shift at an exporting food manufacturer.' },
          { v: '4', k: 'Prior systems built', s: 'Auctions, product orchestration, catering CRM and breeding operations, across four industries.' },
          { v: '10 yrs', k: 'GCC enterprise delivery', s: 'A decade of direct and subcontracted enterprise technology delivery supporting government, defence, diplomatic and banking organisations in Saudi Arabia.' },
        ].map((m, i) => (
          <div key={i} style={{ padding: '6mm 5mm', background: C.paper, borderLeft: `4px solid ${C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '30pt', fontWeight: 700, color: C.navy, lineHeight: 1, letterSpacing: '-0.02em' }}>{m.v}</div>
            <div style={{ fontSize: '10pt', color: C.navy, marginTop: '3mm', fontWeight: 700, lineHeight: 1.35 }}>{m.k}</div>
            <div style={{ fontSize: '9pt', color: C.slate, marginTop: '2mm', lineHeight: 1.55 }}>{m.s}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '8mm', padding: '6mm 7mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
          Why this matters for a nomination
        </div>
        <div style={{ fontSize: '11pt', lineHeight: 1.65, color: 'rgba(255,255,255,0.9)' }}>
          This is not an idea seeking funding to be built. The product exists, it is deployed, it earns revenue, and its roadmap is set by a real factory. The remaining work is distribution, which is exactly what an Abu Dhabi base and ecosystem are for.
        </div>
      </div>
    </div>
    <PageFooter section="Proof" />
  </Page>
);

// ==================== PAGE 5: Why the technology is innovative ====================

const InnovationPage = () => (
  <Page pageNum={5} bg={C.paper}>
    <PageHeader pageNum={5} />
    <div style={{ paddingTop: '18mm' }}>
      <StepBadge n="05" label="Why the technology is innovative" />
      <H2>
        Food-grade depth at an<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>SME price and pace.</em>
      </H2>
      <Body>
        The innovation is not a single feature. It is the combination of food-specific operational depth normally associated with enterprise systems, delivered through a modern, modular cloud architecture at a price and implementation pace accessible to smaller manufacturers. That intersection is what the market has left empty.
      </Body>

      <div style={{ marginTop: '4mm', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '5mm' }}>
        {[
          { h: 'Built inside a factory', b: 'Every screen exists because a production, quality or dispatch team needed it during a live shift, not because it looked good in a specification.' },
          { h: 'Modular by design', b: 'Arabic documentation, halal workflows, GSO labelling and e-invoicing attach to one core as modules, so regional capability is added, never re-built as a fork.' },
          { h: 'The dispatch engine', b: 'Retailer-template dispatch documents and GS1-128 / SSCC labelling, generated automatically. Difficult to deliver economically at this price point through traditional enterprise implementation models.' },
          { h: 'Self-learning by design', b: 'Keystone already uses self-learning logic in everyday operation. Values such as case weights and units are learned for each customer from real usage and applied automatically, with no fixed formula to maintain. Users can always override a suggestion when a one-off situation is different. The same intelligent foundation extends across the platform and creates a natural path to assisted documentation, anomaly detection and plain-language reporting.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.cream, borderTop: `3px solid ${C.magenta}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', color: C.navy, fontWeight: 700, marginBottom: '3mm', letterSpacing: '-0.01em' }}>{c.h}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>{c.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '7mm', padding: '5mm 6mm', background: `rgba(201,169,97,0.14)`, borderLeft: `3px solid ${C.gold}`, fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>
        <strong style={{ color: C.navy }}>The architecture is the strategy.</strong> Because capability attaches to a single core rather than forking the product per country, one lean team can stay credible across the entire GCC.
      </div>

      <div style={{ marginTop: '5mm', padding: '5mm 8mm', background: C.navy, color: '#fff', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '16pt', fontWeight: 700, lineHeight: 1.35, letterSpacing: '-0.012em' }}>
          Keystone learns how a factory works <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>without taking control away from the people who run it.</em>
        </div>
      </div>
    </div>
    <PageFooter section="Innovation" />
  </Page>
);

// ==================== PAGE 6: Why Abu Dhabi ====================

const WhyAbuDhabiPage = () => (
  <Page pageNum={6} bg={C.cream}>
    <PageHeader pageNum={6} />
    <div style={{ paddingTop: '18mm' }}>
      <StepBadge n="06" label="Why Abu Dhabi" />
      <H2>
        Our vertical is already an<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>Abu Dhabi priority.</em>
      </H2>
      <Body>
        Abu Dhabi is deliberately building the place where food and technology meet. Keystone is not general software being forced into an Abu Dhabi story; it is a food-manufacturing technology platform entering a market where food security, industrial innovation and digital transformation are already strategic priorities.
      </Body>

      <div style={{ marginTop: '4mm', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '5mm' }}>
        {[
          { h: 'AGWA food & water cluster', b: 'Abu Dhabi\u2019s AGWA initiative is explicitly intended to build a major food and water economic cluster, attract technology and investment, and support food security.' },
          { h: 'ADAFSA digital agenda', b: 'The Abu Dhabi Agriculture and Food Safety Authority is pursuing AI, data and smart systems across food safety and agriculture, a direct tailwind for traceability technology.' },
          { h: 'Food security', b: 'A national strategic priority that makes audit-grade traceability and cold-chain evidence commercially valuable, not academic.' },
          { h: 'Regional technology base', b: 'A recognised, well-connected headquarters from which to reach UAE and wider GCC manufacturers and localise the product.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', color: C.navy, fontWeight: 700, marginBottom: '2.5mm', letterSpacing: '-0.01em' }}>{c.h}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>{c.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '7mm', padding: '6mm 8mm', background: C.navy, color: '#fff', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18pt', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.012em' }}>
          Advanced food manufacturing, food security and digital transformation are Abu Dhabi&rsquo;s agenda. <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>They are also our product.</em>
        </div>
      </div>
    </div>
    <PageFooter section="Why Abu Dhabi" />
  </Page>
);

// ==================== PAGE 7: Market opportunity ====================

const MarketPage = () => (
  <Page pageNum={7} bg={C.paper}>
    <PageHeader pageNum={7} />
    <div style={{ paddingTop: '18mm' }}>
      <StepBadge n="07" label="UAE and GCC market opportunity" />
      <H2>
        Build in <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>three directions.</em>
      </H2>

      <div style={{ marginTop: '4mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { d: '01', tag: 'HOME', h: 'United Arab Emirates', b: 'Dairy, poultry, prepared foods, bakery, processing and exporters feeding modern retail. The primary near-term market and the first reference customer.' },
          { d: '02', tag: 'OUTWARD', h: 'Saudi Arabia', b: 'An important revenue market, reachable through the founder\u2019s existing Saudi enterprise relationships and ZATCA implementation experience.' },
          { d: '03', tag: 'INWARD', h: 'Ireland & UK', b: 'A validated base of artisan and SME producers that funds and de-risks GCC entry rather than competing with it.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '6mm 5mm', background: C.cream, borderTop: `4px solid ${C.magenta}` }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.22em', color: C.muted, fontWeight: 700, marginBottom: '2mm' }}>DIRECTION {c.d} · {c.tag}</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '16pt', color: C.navy, fontWeight: 700, letterSpacing: '-0.015em', marginBottom: '3mm' }}>{c.h}</div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.55 }}>{c.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '7mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm' }}>
        <div style={{ padding: '6mm 6mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.gold}` }}>
          <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700, marginBottom: '3mm' }}>Business model</div>
          <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>
            Subscription per manufacturing site plus a one-off implementation. Priced for the operations director, not the board, so the sales cycle is weeks and churn is low. Revenue per site rises with each module added.
          </div>
        </div>
        <div style={{ padding: '6mm 6mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.gold}` }}>
          <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700, marginBottom: '3mm' }}>Localisation roadmap</div>
          <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>
            Arabic and bilingual documents, halal workflows, GSO labelling, UAE retailer dispatch templates and Saudi ZATCA e-invoicing, each a module on the existing core, phased over the first two years.
          </div>
        </div>
      </div>

      <div style={{ marginTop: '6mm', padding: '5mm 6mm', background: `rgba(201,169,97,0.14)`, borderLeft: `3px solid ${C.gold}`, fontSize: '9pt', color: C.slate, lineHeight: 1.6 }}>
        A conservative base case reaches roughly twenty active sites across the UAE, GCC, Ireland and the UK by year three, funded by the founder with no external capital required. Full figures are in the accompanying Abu Dhabi Business Plan.
      </div>
    </div>
    <PageFooter section="Market" />
  </Page>
);

// ==================== PAGE 8: Founder ====================

const FounderPage = () => (
  <Page pageNum={8} bg={C.navy} noPad>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <PageHeader pageNum={8} dark />
      <div style={{ paddingTop: '18mm', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 700, color: C.gold, marginBottom: '5mm' }}>
          08 · Founder
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '48mm 1fr', gap: '8mm', alignItems: 'start' }}>
          <div>
            <img
              src="/team/asif.png"
              alt="Mohammed Asif Ali Khan, Founder, Keystone by CloudVoro"
              style={{
                width: '48mm', height: '58mm', objectFit: 'cover', objectPosition: 'center top',
                border: `2px solid ${C.gold}`, boxShadow: '0 6px 18px rgba(0,0,0,0.35)', display: 'block',
              }}
            />
            <div style={{ marginTop: '3mm', fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, fontWeight: 700 }}>
              Founder & CEO
            </div>
          </div>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '28pt', fontWeight: 700, color: '#fff', lineHeight: 1.08, letterSpacing: '-0.022em', margin: 0 }}>
              Mohammed Asif Ali Khan
            </h1>
            <div style={{ marginTop: '3mm', fontSize: '9pt', letterSpacing: '0.14em', textTransform: 'uppercase', color: C.gold, fontWeight: 700 }}>
              Founder &middot; Intellix IT Solutions Ltd &middot; Keystone by CloudVoro
            </div>
            <div style={{ marginTop: '4mm', fontSize: '10pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.65 }}>
              Technology entrepreneur and product builder. Ten years delivering enterprise systems and digital transformation across Ireland, Saudi Arabia and the UK, including ZATCA electronic invoicing on Microsoft AX and SAP, and delivery supporting government, defence, diplomatic and banking organisations.
            </div>
            <div style={{ marginTop: '3mm', fontSize: '10pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.65 }}>
              MSc in IT Enabled Innovation, National University of Ireland Maynooth. Member of the British Computer Society. He will lead GCC product strategy, Abu Dhabi market entry and commercial development personally.
            </div>
          </div>
        </div>

        <div style={{ marginTop: '8mm', padding: '5mm 6mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}` }}>
          <div style={{ fontSize: '8pt', letterSpacing: '0.24em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
            Selected organisations engaged &middot; direct or subcontracted
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2.5mm 6mm', fontSize: '9.5pt', color: 'rgba(255,255,255,0.9)' }}>
            {[
              'Saudi Ministry of Defence', 'Royal Saudi Air Force', 'Indonesian Embassy, Riyadh',
              'QNB KSA', 'Kaspersky', 'Cashel Blue Cheese',
            ].map((o, i) => <div key={i}>· {o}</div>)}
          </div>
        </div>

        <div style={{ marginTop: '7mm', padding: '5mm 8mm', background: C.magenta, color: '#fff', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.012em' }}>
            Built in a factory. Scaling from Abu Dhabi. <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>Sold across the Gulf.</em>
          </div>
        </div>
      </div>
      <PageFooter dark section="Founder" />
    </div>
  </Page>
);

// ==================== PAGE 9: Abu Dhabi establishment plan ====================

const EstablishmentPage = () => (
  <Page pageNum={9} bg={C.cream}>
    <PageHeader pageNum={9} />
    <div style={{ paddingTop: '18mm' }}>
      <StepBadge n="09" label="Abu Dhabi establishment plan" />
      <H2>
        A base built<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>against evidence.</em>
      </H2>
      <Body>
        The founder funds validation first. Local headcount and permanent infrastructure are released only against qualified pipeline and customer demand. The European structure retains the core platform through validation; the long-term corporate and IP arrangement follows professional advice.
      </Body>

      <div style={{ marginTop: '4mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { m: 'Months 1\u20133', tag: 'Enter', b: 'Golden Visa nomination initiated. Incubator or venture-studio relationship established. UAE manufacturer discovery and AGWA ecosystem mapping. First demonstrations.' },
          { m: 'Months 4\u20136', tag: 'Pilot', b: 'First UAE pilot secured. Platform configured for UAE dispatch, GSO labelling and Arabic documentation. Saudi conversations maintained in parallel.' },
          { m: 'Months 7\u201312', tag: 'Commercialise', b: 'First UAE reference customer converted. Additional opportunities progressed. First UAE-based hire evaluated. 24-month plan defined.' },
        ].map((p, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.paper, borderTop: `4px solid ${C.magenta}` }}>
            <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.muted, fontWeight: 700 }}>{p.m}</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', fontWeight: 700, color: C.navy, letterSpacing: '-0.015em', margin: '2mm 0 3mm' }}>{p.tag}</div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.6 }}>{p.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '7mm', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4mm' }}>
        {[
          { h: 'Ireland & UK', s: 'Proven base' },
          { h: 'Abu Dhabi', s: 'GCC headquarters' },
          { h: 'UAE & KSA', s: 'Primary markets' },
          { h: 'Wider GCC', s: 'Qatar, Bahrain, Kuwait, Oman' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '5mm 4mm', background: i === 1 ? C.magenta : C.navy, color: '#fff', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', fontWeight: 700, letterSpacing: '-0.01em' }}>{c.h}</div>
            <div style={{ fontSize: '8pt', letterSpacing: '0.12em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginTop: '2mm' }}>{c.s}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '6mm', padding: '5mm 6mm', background: C.navy, color: 'rgba(255,255,255,0.9)', fontSize: '9.5pt', lineHeight: 1.6 }}>
        The full 15-page Abu Dhabi Business Plan sets out financials in AED, the localisation roadmap, the risk register and the twelve-then-twelve month milestones in detail.
      </div>
    </div>
    <PageFooter section="Establishment" />
  </Page>
);

// ==================== PAGE 10: The ask ====================

const AskPage = () => (
  <Page pageNum={10} bg={C.navy} noPad last>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <PageHeader pageNum={10} dark />
      <div style={{ paddingTop: '18mm', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 700, color: C.gold, marginBottom: '5mm' }}>
          10 · The ask
        </div>
        <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '28pt', fontWeight: 700, color: '#fff', lineHeight: 1.15, letterSpacing: '-0.018em', margin: '0 0 4mm' }}>
          Entrepreneur Golden Visa nomination,<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>and ecosystem entry.</em>
        </h2>
        <p style={{ fontSize: '11pt', color: 'rgba(255,255,255,0.88)', lineHeight: 1.7, margin: '0 0 6mm', maxWidth: '160mm' }}>
          Keystone is a live, revenue-generating product ready to establish its GCC base in Abu Dhabi. We are seeking assessment for the Abu Dhabi Entrepreneur Golden Visa pathway and entry into the food-technology ecosystem.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4mm' }}>
          {[
            { n: '01', h: 'Entrepreneur nomination', b: 'Assessment for the Abu Dhabi Entrepreneur Golden Visa pathway, including routing to the appropriate UAE incubator or venture studio for the acceptance and nomination required under the concept route.' },
            { n: '02', h: 'A regional technology base', b: 'A route to establish Keystone\u2019s GCC headquarters, product localisation and commercial development in Abu Dhabi.' },
            { n: '03', h: 'Food industry access', b: 'Introductions into food manufacturing, processing, quality and traceability, and relevant AGWA ecosystem organisations.' },
            { n: '04', h: 'Commercial ecosystem', b: 'Technology partners, potential customers, business-development resources and appropriate incubation relationships.' },
          ].map((item, i) => (
            <div key={i} style={{ padding: '4mm 5mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}`, display: 'grid', gridTemplateColumns: '13mm 1fr', gap: '3mm', alignItems: 'start' }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22pt', fontWeight: 700, color: C.gold, lineHeight: 1 }}>{item.n}</div>
              <div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '12.5pt', color: '#fff', fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '1.5mm' }}>{item.h}</div>
                <div style={{ fontSize: '9pt', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{item.b}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '6mm', padding: '5mm 8mm', background: C.magenta, color: '#fff', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18pt', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.012em' }}>
            A product that is already working. <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>A base that is ready to build.</em>
          </div>
        </div>

        <div style={{ marginTop: '6mm', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ fontSize: '10pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
            <strong style={{ color: '#fff' }}>Mohammed Asif Ali Khan</strong><br/>
            Founder &amp; CEO, Keystone by CloudVoro<br/>
            asif@cloudvoro.com &middot; keystone.cloudvoro.com
          </div>
          <div style={{ textAlign: 'right' }}>
            <KeystoneLogo variant="dark" size={30} />
          </div>
        </div>

        <div style={{ marginTop: '5mm', paddingTop: '4mm', borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: '7pt', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
          Companion document to the full Abu Dhabi Business Plan. References: ADRO Entrepreneur Golden Visa nomination guidance; ADIO; Hub71; ICP entrepreneur requirements; AGWA food and water cluster; ADAFSA. Figures are management estimates for planning purposes; establishment and any corporate or IP change would follow professional legal and tax advice in Ireland and the UAE.
        </div>
      </div>
      <PageFooter dark section="The ask" />
    </div>
  </Page>
);

// ==================== Root ====================

function ConceptDeckContent() {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadServerPDF('/brochure/concept-deck/ad', 'Keystone-Abu-Dhabi-Concept-Deck.pdf'); }
    catch (e) { alert(e?.message || 'PDF render failed'); }
    setBusy(false);
  };

  return (
    <div style={{ background: '#F1F5F9', minHeight: '100vh', padding: '24px 0' }} data-testid="brochure-concept-deck-ad">
      <div style={{
        position: 'sticky', top: 12, zIndex: 10, maxWidth: '210mm', margin: '0 auto 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
        padding: '10px 16px', boxShadow: '0 4px 16px rgba(10,37,64,0.08)',
      }} className="brochure-actions no-print">
        <div style={{ fontSize: 13, color: C.slate }}>
          <strong style={{ color: C.navy }}>Concept Deck</strong> &middot; Abu Dhabi &middot; confidential
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link
            to="/brochure/bplan/ad"
            data-testid="brochure-concept-deck-ad-bplan-link"
            style={{ padding: '10px 18px', background: '#fff', color: C.navy, border: `1px solid ${C.navy}`, borderRadius: 4, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none' }}
          >
            &larr; Full business plan
          </Link>
          <button
            onClick={download}
            disabled={busy}
            data-testid="brochure-concept-deck-ad-download-pdf"
            style={{ padding: '10px 18px', background: C.magenta, color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}
          >
            {busy ? 'Rendering PDF\u2026' : 'Download PDF'}
          </button>
        </div>
      </div>

      <CoverPage />
      <ProblemPage />
      <WhatPage />
      <ProofPage />
      <InnovationPage />
      <WhyAbuDhabiPage />
      <MarketPage />
      <FounderPage />
      <EstablishmentPage />
      <AskPage />

      <style>{`
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          #root, #root > * { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .brochure-page {
            margin: 0 !important;
            box-shadow: none !important;
            border: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden !important;
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .brochure-page:last-of-type {
            page-break-after: auto;
            break-after: auto;
          }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  );
}

export default function BrochureConceptDeckAD() {
  return (
    <BrochureGate
      title="Concept deck · Abu Dhabi · private."
      subtitle="This is a confidential Keystone concept deck prepared for the Abu Dhabi Entrepreneur nomination pathway. Enter the access password to continue."
      buttonLabel="Unlock concept deck"
      eyebrow="Keystone by CloudVoro · Confidential · Abu Dhabi"
    >
      <ConceptDeckContent />
    </BrochureGate>
  );
}
